/**
 * Catálogo de zonas horarias IANA para selectores de "Zona horaria" del dashboard.
 * Compartido por `Locations.tsx` y `Schedule.tsx` (ambos escriben/leen el mismo
 * campo `Location.timezone`, así que deben ofrecer el mismo catálogo) a través
 * del componente `TimezoneSelect`.
 */

// Zonas horarias más habituales entre nuestros clientes (España + LatAm).
// Se muestran primero en cualquier selector de zona horaria (grupo "Frecuentes").
export const FRECUENTES_TZ: string[] = [
  "Europe/Madrid",
  "Atlantic/Canary",
  "America/Mexico_City",
  "America/Argentina/Buenos_Aires",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "Europe/Lisbon",
  "Europe/London",
];

// Override manual SOLO del nombre de ciudad, para las pocas zonas frecuentes
// cuyo segmento IANA en crudo no se lee bien en español (p.ej. "Canary" en vez
// de "Canarias"). El resto de la etiqueta (nombre de zona + GMT) siempre se
// genera con Intl — no hay una segunda fuente de etiquetas "a mano".
const CIUDAD_LABELS_ES: Record<string, string> = {
  "Europe/Madrid": "Madrid",
  "Atlantic/Canary": "Canarias",
  "America/Mexico_City": "Ciudad de México",
  "America/Argentina/Buenos_Aires": "Buenos Aires",
  "America/Bogota": "Bogotá",
  "America/Lima": "Lima",
  "America/Santiago": "Santiago",
  "Europe/Lisbon": "Lisboa",
  "Europe/London": "Londres",
};

const CONTINENTE_LABELS: Record<string, string> = {
  Africa: "África",
  America: "América",
  Antarctica: "Antártida",
  Arctic: "Ártico",
  Asia: "Asia",
  Atlantic: "Atlántico",
  Australia: "Australia y Oceanía",
  Europe: "Europa",
  Indian: "Océano Índico",
  Pacific: "Pacífico",
};

// `Intl.supportedValuesOf` es ES2022; el tsconfig del dashboard usa lib ES2020,
// así que se comprueba en runtime con un tipo local en vez de tocar el lib global.
type IntlWithSupportedValuesOf = typeof Intl & {
  supportedValuesOf?: (key: string) => string[];
};

export function getAllTimezones(): string[] {
  try {
    const intlExt = Intl as IntlWithSupportedValuesOf;
    if (typeof intlExt.supportedValuesOf === "function") {
      return intlExt.supportedValuesOf("timeZone");
    }
  } catch {
    /* navegador sin soporte: cae al fallback de abajo */
  }
  return [...FRECUENTES_TZ];
}

export interface TimezoneGroup {
  continente: string;
  zonas: string[];
}

export function buildTimezoneGroups(): TimezoneGroup[] {
  const resto = getAllTimezones().filter((tz) => !FRECUENTES_TZ.includes(tz));
  const porContinente = new Map<string, string[]>();
  for (const tz of resto) {
    const continente = tz.split("/")[0];
    const lista = porContinente.get(continente) ?? [];
    lista.push(tz);
    porContinente.set(continente, lista);
  }
  return Array.from(porContinente.entries())
    .map(([continente, zonas]) => ({
      continente: CONTINENTE_LABELS[continente] || continente,
      zonas: [...zonas].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.continente.localeCompare(b.continente, "es"));
}

// Se calcula una sola vez al cargar el módulo: agrupar/ordenar los 417
// identificadores es barato (solo `split`/`sort` de strings). Lo caro —y por
// tanto perezoso, ver más abajo— es generar la ETIQUETA legible de cada zona.
export const TIMEZONE_GROUPS = buildTimezoneGroups();

export function getBrowserTimezone(): string {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid"
    );
  } catch {
    return "Europe/Madrid";
  }
}

/** True si `tz` no aparece ni en Frecuentes ni en los grupos por continente. */
export function isUnknownTimezone(tz: string): boolean {
  if (!tz) return false;
  if (FRECUENTES_TZ.includes(tz)) return false;
  return !TIMEZONE_GROUPS.some((g) => g.zonas.includes(tz));
}

// ---------------------------------------------------------------------------
// Etiquetas legibles en español ("Ciudad (nombre de la zona) GMT±X")
// ---------------------------------------------------------------------------

function capitalizeFirst(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

function getCityFromTimezone(tz: string): string {
  if (CIUDAD_LABELS_ES[tz]) return CIUDAD_LABELS_ES[tz];
  const segmentos = tz.split("/");
  const ultimo = segmentos[segmentos.length - 1] || tz;
  return ultimo.replace(/_/g, " ");
}

function getSpanishZoneName(tz: string): string {
  const parts = new Intl.DateTimeFormat("es-ES", {
    timeZone: tz,
    timeZoneName: "long",
  }).formatToParts(new Date());
  const nombre = parts.find((p) => p.type === "timeZoneName")?.value;
  return nombre ? capitalizeFirst(nombre) : "";
}

function getOffsetLabel(tz: string): string {
  const parts = new Intl.DateTimeFormat("es-ES", {
    timeZone: tz,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value || "";
}

// Caché: cada llamada a `Intl.DateTimeFormat` + `formatToParts` tiene un coste
// real (~0.15ms por zona x2 formatters); con 417 zonas, calcularlas todas de
// golpe puede bloquear el hilo principal 300-500ms en un móvil de gama baja.
// Por eso NUNCA se recorren las 417 en la carga del módulo: cada etiqueta se
// calcula la primera vez que se pide y se memoiza aquí.
const timezoneLabelCache = new Map<string, string>();

/**
 * Etiqueta legible en español de una zona IANA: "Ciudad (nombre de la zona) GMT±X".
 * Ej: "Canarias (Hora de verano de Europa occidental) GMT+1".
 * Si Intl falla para el identificador (no debería, pero por si acaso), devuelve
 * el identificador IANA tal cual.
 */
export function getTimezoneLabel(tz: string): string {
  const cached = timezoneLabelCache.get(tz);
  if (cached) return cached;

  let label = tz;
  try {
    const ciudad = getCityFromTimezone(tz);
    const nombreEs = getSpanishZoneName(tz);
    const offset = getOffsetLabel(tz);
    const partes = [ciudad];
    if (nombreEs) partes.push(`(${nombreEs})`);
    if (offset) partes.push(offset);
    if (partes.length > 0) label = partes.join(" ");
  } catch {
    label = tz;
  }

  timezoneLabelCache.set(tz, label);
  return label;
}

// ---------------------------------------------------------------------------
// Búsqueda
// ---------------------------------------------------------------------------

/** Minúsculas y sin acentos, para comparar "mexico" con "México". */
export function normalizeForSearch(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** True si `tz` coincide con la búsqueda por etiqueta española o identificador. */
export function timezoneMatchesSearch(tz: string, query: string): boolean {
  const q = normalizeForSearch(query.trim());
  if (!q) return true;
  const haystack = normalizeForSearch(`${tz} ${getTimezoneLabel(tz)}`);
  return haystack.includes(q);
}

// ---------------------------------------------------------------------------
// Precalentado en segundo plano
// ---------------------------------------------------------------------------

function scheduleIdle(callback: () => void): void {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => callback());
  } else {
    setTimeout(callback, 50);
  }
}

let warmupScheduled = false;

/**
 * Calienta la caché de etiquetas del resto de zonas (las no-Frecuentes) en
 * lotes pequeños durante los ratos libres del navegador, sin bloquear nada.
 * Idempotente: llamarla varias veces (un `TimezoneSelect` por página, dos
 * páginas con selector) no repite el trabajo.
 */
export function warmTimezoneLabelsInBackground(): void {
  if (warmupScheduled) return;
  warmupScheduled = true;

  const zonas = getAllTimezones();
  const LOTE = 40;
  let indice = 0;

  const procesarLote = (): void => {
    const limite = Math.min(indice + LOTE, zonas.length);
    for (; indice < limite; indice++) {
      getTimezoneLabel(zonas[indice]);
    }
    if (indice < zonas.length) {
      scheduleIdle(procesarLote);
    }
  };

  scheduleIdle(procesarLote);
}
